/**
 * The session lifecycle, as a pure reducer. The single place that decides what an auth signal
 * MEANS for the session; the Supabase client, the HTTP transport and the router only report to
 * it or read from it.
 *
 * ## Why this is a reducer and not a set of booleans in a store
 *
 * **Terminality is structural.** From any live state only two events reach `signedOut`: the
 * trainer's own `USER_LOGOUT`, and `SUPABASE_SIGNED_OUT` — supabase-js's own conclusion that a
 * non-retryable refresh failed against an already-expired token. A network failure, a 5xx, a
 * storage read that threw, or a 401 that survived the transport's refresh-and-retry can at
 * worst reach `degraded`, where the session is RETAINED and cached content keeps rendering.
 *
 * That is not a style preference. The mobile app learned it from three separate forced-logout
 * incident classes (#319 / #324 / #332), each of which was a different code path deciding on
 * its own that a failure meant "log the user out". Encoding the rule in the transition table
 * means no future caller can reintroduce it: there is no event that expresses "sign out because
 * something failed", so nothing can dispatch one.
 *
 * ## States
 *
 * | State        | Meaning                                                                    |
 * |--------------|----------------------------------------------------------------------------|
 * | `restoring`  | Cold start; the persisted session has not been resolved yet. No route may decide. |
 * | `signedIn`   | A live session belonging to a **PersonalTrainer**. The app may render.      |
 * | `incomplete` | A live, valid session with no local profile row (`profile_missing`). Profile completion. |
 * | `refused`    | A live Supabase identity whose backend role is not PersonalTrainer. Terminal for this app. |
 * | `degraded`   | We HAVE a session but auth is transiently failing. Never a reason to sign in again. |
 * | `signedOut`  | No session. `endReason` tells the sign-in screen whether it expired or was deliberate. |
 *
 * Pure: no React, no DOM, no storage, no i18n. The whole transition table is pinned by
 * `sessionMachine.test.ts`.
 */

/** Backend role names, from `train-with-me/Domain/RoleNames.cs`. */
export type UserRole = 'PersonalTrainer' | 'Admin' | 'Customer';

/** The signed-in trainer, flattened from `GET /auth/me`. */
export interface SessionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  /** "firstName lastName", or the email when no name is set. */
  displayName: string;
  role: UserRole;
  avatarUrl: string | null;
  /** The trainer's own row id — the path parameter for every `/personaltrainer/{id}` call. */
  trainerId: number | null;
  /** The trainer is on the nutrition pilot (nutrition R16): the client detail shows Odżywianie. */
  nutritionEnabled: boolean;
  /**
   * The trainer's own client profile („TY", trainer-self-client R3), or `null`. Nobody can message
   * it: the backend refuses a conversation with yourself.
   */
  selfCustomerId: number | null;
}

export type SessionStatus =
  | 'restoring'
  | 'signedIn'
  | 'incomplete'
  | 'refused'
  | 'degraded'
  | 'signedOut';

/**
 * Why the previous session ended. Read ONCE by the sign-in screen, then consumed — a deliberate
 * logout must never leave a "your session expired" banner behind, which is the whole reason
 * `USER_LOGOUT` has to be dispatched before `signOut()` is awaited (see `sessionController.ts`).
 */
export type SessionEndReason = 'userLogout' | 'expired';

export interface SessionState {
  status: SessionStatus;
  /** The trainer. Non-null in `signedIn`, and retained through a `degraded` episode. */
  user: SessionUser | null;
  /** The role the backend reported when it refused us. The refusal screen renders it. */
  refusedRole: UserRole | null;
  endReason: SessionEndReason | null;
}

export type SessionEvent =
  /** `GET /auth/me` resolved with `role: "PersonalTrainer"`. */
  | { type: 'HYDRATED'; user: SessionUser }
  /**
   * `GET /auth/me` resolved, but with some other role. Asserted positively — see `hydration.ts`.
   * `role` is `null` when the backend named a role this app does not recognise, which is still a
   * refusal: only a positive `PersonalTrainer` opens the app.
   */
  | { type: 'ROLE_REFUSED'; role: UserRole | null }
  /** `GET /auth/me` answered `profile_missing`: the token is valid, the profile row is not there. */
  | { type: 'PROFILE_MISSING' }
  /** Cold start found no persisted session — or could not read storage at all. */
  | { type: 'BOOT_NO_SESSION' }
  /** Cold start found a session, but hydration was inconclusive (offline, 5xx, backend restart). */
  | { type: 'BOOT_HYDRATION_INCONCLUSIVE' }
  /** The transport saw an authenticated call fail auth even after one refresh and one retry. */
  | { type: 'AUTH_TRANSIENT_FAILURE' }
  /** A degraded session proved healthy again (an authenticated 2xx, or a token refresh). */
  | { type: 'SESSION_RECOVERED' }
  /** supabase-js concluded the session is gone. The only terminal signal we do not originate. */
  | { type: 'SUPABASE_SIGNED_OUT' }
  /** The trainer asked to sign out. The only other terminal signal. */
  | { type: 'USER_LOGOUT' }
  /** The sign-in screen rendered `endReason`. One-shot, so a later visit does not re-show it. */
  | { type: 'END_REASON_CONSUMED' };

export const INITIAL_SESSION_STATE: SessionState = {
  status: 'restoring',
  user: null,
  refusedRole: null,
  endReason: null,
};

export function sessionReducer(state: SessionState, event: SessionEvent): SessionState {
  switch (event.type) {
    case 'HYDRATED':
      // Reachable from every state, `signedOut` included: that is a fresh sign-in. Clears the
      // refusal and the end reason, so signing in as a trainer after being refused is clean.
      return { status: 'signedIn', user: event.user, refusedRole: null, endReason: null };

    case 'ROLE_REFUSED':
      // Unguarded, like HYDRATED, and for the same reason: the status during a fresh sign-in is
      // `signedOut` right up until hydration answers, so a "not while signed out" guard here would
      // silently drop the very outcome it exists to describe. Staleness — an answer arriving after a
      // logout — is the controller's problem, not the reducer's; it never dispatches an answer that
      // belongs to a session that has since ended.
      //
      // `user` is dropped: a refused identity is not a user of this app, and nothing may render its
      // name.
      return { status: 'refused', user: null, refusedRole: event.role, endReason: null };

    case 'PROFILE_MISSING':
      return { status: 'incomplete', user: null, refusedRole: null, endReason: null };

    case 'BOOT_NO_SESSION':
      // Boot events are restoring-only. The ONLY non-terminal path to `signedOut`, and it can
      // only fire before any session was ever established.
      return state.status === 'restoring'
        ? { ...INITIAL_SESSION_STATE, status: 'signedOut' }
        : state;

    case 'BOOT_HYDRATION_INCONCLUSIVE':
      // A session IS in storage and we could not confirm it. Degraded, never the sign-in screen:
      // an offline cold start that signs the trainer out is the incident this rule exists for.
      return state.status === 'restoring'
        ? { ...INITIAL_SESSION_STATE, status: 'degraded' }
        : state;

    case 'AUTH_TRANSIENT_FAILURE':
      // Retains `user`, so cached content keeps rendering under a reconnect banner instead of
      // every screen stranding on its own error. Ignored outside a live, working session:
      // `refused` is terminal for this app and makes no authenticated calls to fail.
      return state.status === 'signedIn' ||
        state.status === 'degraded' ||
        state.status === 'incomplete'
        ? { ...state, status: 'degraded' }
        : state;

    case 'SESSION_RECOVERED':
      // Only a degraded session that still HAS its user can flip straight back. A boot-degraded
      // one (user null) has nothing to render, so it must recover through re-hydration, which
      // arrives as HYDRATED / ROLE_REFUSED / PROFILE_MISSING and carries a fresh answer.
      return state.status === 'degraded' && state.user !== null
        ? { ...state, status: 'signedIn' }
        : state;

    case 'SUPABASE_SIGNED_OUT':
      // Ignored when already signed out, so the trailing SIGNED_OUT that `signOut()` itself emits
      // cannot overwrite `userLogout` with `expired` and produce a bogus "session expired" banner.
      // Ignored in `refused` for the same reason: the refusal path signs out deliberately, and its
      // own event must not erase the reason the trainer is being shown that screen.
      return state.status === 'signedOut' || state.status === 'refused'
        ? state
        : { status: 'signedOut', user: null, refusedRole: null, endReason: 'expired' };

    case 'USER_LOGOUT':
      return { status: 'signedOut', user: null, refusedRole: null, endReason: 'userLogout' };

    case 'END_REASON_CONSUMED':
      return state.endReason === null ? state : { ...state, endReason: null };
  }
}
